import React, { useRef, useState } from "react";
import { api, formatApiErrorDetail, fileToBase64 } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Upload, X, Shield } from "lucide-react";

const STATUS_OPTIONS = [
  { v: "submitted", l: "Submitted" },
  { v: "under_review", l: "Under Review" },
  { v: "settled", l: "Settled" },
  { v: "rejected", l: "Rejected" },
];

export default function StepClosure({ data, reload }) {
  const { shipment, delivery, claim } = data;
  const isGood = delivery?.condition === "good";
  const isDamaged = delivery?.condition === "damaged";

  if (shipment.status === "completed" && isGood) {
    return (
      <div className="text-center py-6">
        <CheckCircle2 className="w-10 h-10 text-[#6B0F1A] mx-auto" />
        <div className="font-display text-lg font-bold mt-2">Shipment closed</div>
        <div className="text-sm text-stone-500">Cargo delivered in good condition.</div>
      </div>
    );
  }

  if (isGood) {
    return <CloseGood shipment={shipment} reload={reload} />;
  }

  if (isDamaged) {
    return <ClaimForm shipment={shipment} claim={claim} reload={reload} />;
  }

  return <div className="text-sm text-stone-500">Complete delivery step first.</div>;
}

function CloseGood({ shipment, reload }) {
  const [busy, setBusy] = useState(false);
  const close = async () => {
    setBusy(true);
    try {
      await api.post(`/shipments/${shipment.id}/close`);
      toast.success("Shipment closed");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-widest text-stone-400">Step 7</div>
      <h2 className="font-display text-xl font-bold tracking-tight text-[#1F1F1F] mt-1">Shipment Closure</h2>
      <p className="text-sm text-stone-500 mb-4">Cargo delivered in good condition. Finalize and close.</p>
      <button onClick={close} disabled={busy} className="btn-primary" data-testid="close-shipment">
        <Shield className="w-4 h-4" /> {busy ? "Closing…" : "Close shipment"}
      </button>
      <style>{`
        .btn-primary { display:inline-flex; align-items:center; gap:.4rem; background:#6B0F1A; color:white; font-weight:600; border-radius:.75rem; padding:.625rem 1rem; font-size:.875rem; }
        .btn-primary:hover:not(:disabled) { background:#56121C; }
        .btn-primary:disabled { opacity:.6; }
      `}</style>
    </div>
  );
}

function ClaimForm({ shipment, claim, reload }) {
  const [form, setForm] = useState({
    nature_of_damage: claim?.nature_of_damage || "",
    claim_amount: claim?.claim_amount || 0,
    currency: claim?.currency || "USD",
    supporting_docs: claim?.supporting_docs || [],
    status: claim?.status || "submitted",
  });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const addDoc = async (file) => {
    if (!file) return;
    const b64 = await fileToBase64(file);
    setForm((f) => ({ ...f, supporting_docs: [...f.supporting_docs, { filename: file.name, content_base64: b64 }] }));
  };
  const removeDoc = (i) => setForm((f) => ({ ...f, supporting_docs: f.supporting_docs.filter((_, idx) => idx !== i) }));

  const saveClaim = async () => {
    setBusy(true);
    try {
      await api.put(`/shipments/${shipment.id}/claim`, {
        ...form,
        claim_amount: parseFloat(form.claim_amount),
      });
      toast.success("Claim saved");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const updateStatus = async (status) => {
    try {
      await api.patch(`/shipments/${shipment.id}/claim/status`, { status });
      toast.success("Claim status updated");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-widest text-amber-600">Claim Module</div>
      <h2 className="font-display text-xl font-bold tracking-tight text-[#1F1F1F] mt-1 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-600" /> Damage Claim
      </h2>
      <p className="text-sm text-stone-500 mb-4">Cargo condition was marked as damaged. Record the claim below.</p>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {STATUS_OPTIONS.map((o) => (
          <button
            key={o.v}
            onClick={() => updateStatus(o.v)}
            data-testid={`claim-status-${o.v}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${
              form.status === o.v || claim?.status === o.v
                ? "bg-[#6B0F1A] text-white border-[#6B0F1A]"
                : "bg-white text-stone-600 border-stone-200 hover:border-[#6B0F1A]"
            }`}
          >
            {o.l}
          </button>
        ))}
      </div>

      <Field label="Nature of Damage">
        <textarea rows={3} value={form.nature_of_damage} onChange={(e) => setForm({ ...form, nature_of_damage: e.target.value })} className="input" data-testid="claim-damage" />
      </Field>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <Field label="Claim Amount">
          <input type="number" value={form.claim_amount} onChange={(e) => setForm({ ...form, claim_amount: e.target.value })} className="input" data-testid="claim-amount" />
        </Field>
        <Field label="Currency">
          <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="input" data-testid="claim-currency">
            {["USD", "EUR", "GBP"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
      </div>

      <div className="mt-4">
        <div className="text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-2">Supporting Documents</div>
        <div className="space-y-2">
          {form.supporting_docs.map((d, i) => (
            <div key={i} className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm">
              <span className="truncate">{d.filename}</span>
              <button onClick={() => removeDoc(i)} data-testid={`remove-claim-doc-${i}`}><X className="w-4 h-4 text-stone-400" /></button>
            </div>
          ))}
          <button onClick={() => fileRef.current?.click()} className="w-full rounded-lg border-2 border-dashed border-stone-300 py-3 text-xs text-stone-500 hover:border-[#A8323F]" data-testid="upload-claim-doc">
            <Upload className="w-4 h-4 inline mr-1" /> Add supporting document
          </button>
          <input ref={fileRef} type="file" hidden onChange={(e) => addDoc(e.target.files?.[0])} />
        </div>
      </div>

      <div className="mt-4">
        <button onClick={saveClaim} disabled={busy} className="btn-primary" data-testid="claim-save">
          <Shield className="w-4 h-4" /> {busy ? "Saving…" : "Save claim"}
        </button>
      </div>
      <style>{`
        .input { width:100%; background:#FBFAF9; border:1px solid #E5E0DC; border-radius:.5rem; padding:.6rem .9rem; color:#1F1F1F; outline:none; font-size: 0.875rem; }
        .input:focus { background:white; border-color:#A8323F; box-shadow:0 0 0 3px rgba(168,50,63,0.15);}
        .btn-primary { display:inline-flex; align-items:center; gap:.4rem; background:#6B0F1A; color:white; font-weight:600; border-radius:.75rem; padding:.625rem 1rem; font-size:.875rem; }
        .btn-primary:hover:not(:disabled) { background:#56121C; }
        .btn-primary:disabled { opacity:.6; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-widest text-stone-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
